import React, {
  useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import { FieldSelect, FieldToggle, FieldSelectOptimized } from 'components/molecules';
import { queryNewsCategories, queryNewsChannel, queryNewsTags } from 'services/requests';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Toast } from 'containers';
import { useParams } from 'react-router-dom'

const NewsSchema = Yup.object().shape({
  categories: Yup.array(
    Yup.number()),
  channel: Yup.number(),
  featured: Yup.boolean(),
  tag: Yup.array(Yup.number())
})

const computeOptions = (opts, valueKey = 'id', labelKey = 'name', tabLabelKey = 'text') => opts.map((option) => ({
  value: option[valueKey],
  label: option[labelKey] || option[tabLabelKey],
  original: option
}))

const NewsEditor = ({ initialProps, onChange }) => {
  const [categories, setCategories] = useState([]);
  const [tag, setTag] = useState([]);
  const [channels, setChannel] = useState([]);
  const [isLoading, setLoading] = useState(true)
  const { lang } = useParams()

  const initialValues = useMemo(() => {
    const categoriesToIgnore = initialProps.categoriesToIgnore || []
    let initialCategories = initialProps.categories || []

    if (
      initialProps.categoriesToIgnore
      && initialProps.categoriesToIgnore.length > 0
      && categories
      && categories.length > 0
    ) {
      // removes ignored categories
      const initialCategoriesFiltered = categories.filter(
        ({ value }) => !categoriesToIgnore.includes(value)
      )
      initialCategories = initialCategoriesFiltered.map(({ value }) => value)
    }
    return {
      featured: initialProps.featured,
      channel: initialProps.channel,
      categories: initialCategories,
      tag: initialProps.tag
    }
  }, [initialProps, categories])

  useEffect(() => {
    const getNewsData = async () => {
      try {
        setLoading(true)

        const { data: { data: catData } } = await queryNewsCategories({ language: lang });
        const { data: { data: channelData } } = await queryNewsChannel({ language: lang });
        const { data: tabData } = await queryNewsTags({ language: lang });

        setCategories(computeOptions(catData));
        setChannel(computeOptions(channelData));
        setTag(computeOptions(tabData));

        setLoading(false)
      } catch (err) {
        setLoading(false)
        toast.error(
          <Toast
            type="error"
            title="Erro a carregar formulário de Notícias"
            description={err.message}
          />
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
    getNewsData();
  }, [lang]);

  const handleFormChange = (newValues) => {
    const newProps = {
      ...initialProps, ...newValues, categoriesToIgnore: []
    };
    onChange(newProps);
  }

  if (isLoading) {
    return (
      <div>
      A carregar...
      </div>
    )
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={NewsSchema}
        validate={handleFormChange}
        enableReinitialize
      >
        <Form>
          <Field
            title="Categorias"
            name="categories"
            options={categories}
            component={FieldSelect}
            placeholder="Selecione categorias..."
            isMulti
          />
          <Field
            title="Tags"
            name="tag"
            options={tag}
            component={FieldSelectOptimized}
            placeholder="Selecione tags..."
            isMulti
          />
          <Field
            title="Canal"
            name="channel"
            placeholder="Selecione um canal.."
            info="Indique o canal onde pretende obter as suas notícias. Este pode ser o canal geral da UA, do seu departamento, subweb, ou qualquer outra escolha."
            component={FieldSelect}
            options={channels}
          />
          <Field
            title="Mostrar apenas destaques"
            name="featured"
            component={FieldToggle}
          />
        </Form>
      </Formik>
    </div>
  );
}

NewsEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    featured: PropTypes.bool,
    channel: PropTypes.array,
    recordsPerPage: PropTypes.array,
    categoriesToIgnore: PropTypes.array,
    categories: PropTypes.array,
    tag: PropTypes.array,
  }),
};
NewsEditor.defaultProps = {
  initialProps: {},
};

export default NewsEditor
